// Single source of truth for every public route's search metadata.
// Used at runtime by <Seo /> and at build time by scripts/prerender.mjs, which
// writes each path below to dist/<path>/index.html with these tags baked in and
// generates sitemap.xml from the indexable entries.
//
// Paths use a trailing slash: Apache serves the prerendered directories and
// 301s "/programs" to "/programs/", so the slash form is the canonical one.
//
// `sources` lists the files whose last git commit date becomes the sitemap
// <lastmod>, so the date only moves when the page content really changes.

import { SITE_URL } from "./site.js";

// Per-page social share card (1200x630 JPEG in public/og/, generated by
// scripts/og-cards/make-og-cards.php). Every page gets its own card so shared
// links don't all show the same homepage photo.
const card = (name, alt) => ({
  url: `${SITE_URL}/og/${name}.jpg`,
  type: "image/jpeg",
  width: 1200,
  height: 630,
  alt,
});

// Tamil-language pages go live only after a fluent Tamil reviewer has checked
// the wording (see OWNER_INPUT_NEEDED.md). Until then the Tamil page exists
// only in `npm run dev`: no production route, no prerendered file, no sitemap
// entry and no hreflang. Set to true once reviewed, then rebuild and deploy.
export const TAMIL_PAGES_REVIEWED = false;

const TAMIL_CLASSES_PATH = "/ta/tamil-classes-sydney/";

// Tamil version of the classes page (src/Pages/TamilClassesTa.jsx).
export const TAMIL_CLASSES_PAGE = {
  path: TAMIL_CLASSES_PATH,
  image: card("tamil-classes-sydney", "Tamil Classes in Sydney - Western Sydney Tamil Study Centre"),
  lang: "ta",
  ogLocale: "ta_IN",
  title: "சிட்னியில் குழந்தைகளுக்கான தமிழ் வகுப்புகள் | WSTSC",
  description:
    "பிளாக்டவுன் Walters Road Public School வளாகத்தில் குழந்தைகளுக்கான தமிழ் மொழி, பண்பாட்டு வகுப்புகள். யார் சேரலாம், என்ன கற்பிக்கப்படுகிறது, எப்படிச் சேர்வது.",
  h1: "சிட்னியில் குழந்தைகளுக்கான தமிழ் வகுப்புகள்",
  breadcrumbs: [{ name: "தமிழ் வகுப்புகள்" }],
  schema: ["organization"],
  alternates: [{ hreflang: "en-AU", path: "/tamil-classes-sydney/" }],
  xDefault: "/tamil-classes-sydney/",
  noindex: !TAMIL_PAGES_REVIEWED,
  sources: ["src/Pages/TamilClassesTa.jsx"],
};

export const PAGES = [
  {
    path: "/",
    image: card("home", "Tamil Classes for Children in Sydney - Western Sydney Tamil Study Centre"),
    title: "Western Sydney Tamil Study Centre | Tamil School in Blacktown",
    description:
      "Tamil language and culture classes for children at Walters Road Public School, Blacktown. A not-for-profit community language school in Western Sydney.",
    breadcrumbs: [],
    schema: ["organization", "website"],
    sources: ["src/Pages/Home.jsx", "src/constants/data.jsx"],
  },
  {
    path: "/tamil-classes-sydney/",
    image: card("tamil-classes-sydney", "Tamil Classes in Sydney - Western Sydney Tamil Study Centre"),
    title: "Tamil Classes in Sydney | Western Sydney Tamil Study Centre",
    description:
      "Children's Tamil classes in Western Sydney at Walters Road Public School, Blacktown. Who the classes suit, what children learn and how to enrol.",
    h1: "Tamil Classes in Sydney",
    breadcrumbs: [{ name: "Tamil Classes in Sydney" }],
    schema: ["organization"],
    // Reciprocal hreflang with the Tamil version, only once it is published.
    ...(TAMIL_PAGES_REVIEWED && {
      alternates: [{ hreflang: "ta", path: TAMIL_CLASSES_PATH }],
      xDefault: "/tamil-classes-sydney/",
    }),
    sources: ["src/Pages/TamilClassesSydney.jsx"],
  },
  {
    path: "/programs/",
    image: card("programs", "Tamil Learning Levels, Preschool to Year 12 - WSTSC"),
    title: "Tamil Learning Levels, Preschool to Year 12 | WSTSC Blacktown",
    description:
      "How WSTSC builds Tamil listening, speaking, reading and writing from Preschool to Year 12, with culture, values and history at every stage.",
    h1: "Tamil Learning Levels and Programs",
    breadcrumbs: [{ name: "Programs and Levels" }],
    sources: ["src/Pages/Programs.jsx", "src/constants/data.jsx"],
  },
  {
    path: "/enrolment/",
    image: card("enrolment", "How to Enrol - Western Sydney Tamil Study Centre"),
    title: "How to Enrol in Tamil Classes | WSTSC Blacktown",
    description:
      "Enrol your child in Tamil classes at Western Sydney Tamil Study Centre: submit an expression of interest online and we will email you the next steps.",
    h1: "How to Enrol",
    breadcrumbs: [{ name: "How to Enrol" }],
    sources: ["src/Pages/Enrolment.jsx"],
  },
  {
    path: "/contact/",
    image: card("contact", "Contact Us and Find Our Venue - WSTSC, Blacktown"),
    title: "Contact WSTSC | Venue and Directions, Blacktown",
    description:
      "Contact Western Sydney Tamil Study Centre by phone or email, and find our venue at Walters Road Public School, 158 Walters Road, Blacktown NSW 2148.",
    h1: "Contact Us and Find Our Venue",
    breadcrumbs: [{ name: "Contact and Venue" }],
    schema: ["organization"],
    sources: ["src/Pages/Contact.jsx", "src/Components/ContactUs.jsx"],
  },
  {
    path: "/about/",
    image: card("about", "About Western Sydney Tamil Study Centre"),
    title: "About WSTSC | Tamil Language and Culture School, Western Sydney",
    description:
      "Our mission, values and teaching approach: how Western Sydney Tamil Study Centre helps children grow in Tamil language, culture and confidence.",
    h1: "About Western Sydney Tamil Study Centre",
    breadcrumbs: [{ name: "About Us" }],
    schema: ["organization"],
    sources: ["src/Pages/About.jsx", "src/constants/data.jsx"],
  },
  {
    path: "/events/",
    image: card("events", "Events and News - Western Sydney Tamil Study Centre"),
    title: "Events and News | Western Sydney Tamil Study Centre",
    description:
      "Upcoming events and celebrations at Western Sydney Tamil Study Centre, the community Tamil school at Walters Road Public School, Blacktown.",
    h1: "Events and News",
    breadcrumbs: [{ name: "Events" }],
    sources: ["src/Pages/Events.jsx"],
  },
  {
    path: "/eoi/",
    image: card("eoi", "Enrolment Expression of Interest - WSTSC"),
    title: "Enrolment Expression of Interest Form | WSTSC",
    description:
      "Register your interest in Tamil classes at Western Sydney Tamil Study Centre, Blacktown. Verify your mobile number, add your children's details and submit.",
    h1: "Enrolment Expression of Interest",
    breadcrumbs: [
      { name: "How to Enrol", path: "/enrolment/" },
      { name: "Expression of Interest" },
    ],
    sources: ["src/Pages/ExpressionInterestForm.jsx"],
  },
  {
    path: "/tamil-anthem/",
    image: card("tamil-anthem", "Tamil Language Anthem - Western Sydney Tamil Study Centre"),
    title: "தமிழ்மொழி வாழ்த்து | Tamil Language Anthem Lyrics | WSTSC",
    description:
      "Full Tamil lyrics of தமிழ்மொழி வாழ்த்து, the Tamil language anthem sung at Western Sydney Tamil Study Centre.",
    breadcrumbs: [{ name: "தமிழ்மொழி வாழ்த்து" }],
    sources: ["src/Pages/TamilAntham.jsx", "src/constants/data.jsx"],
  },
  {
    path: "/coming-soon/",
    title: "Coming Soon | Western Sydney Tamil Study Centre",
    description: "This page is being prepared.",
    noindex: true,
    breadcrumbs: [{ name: "Coming Soon" }],
  },
  ...(TAMIL_PAGES_REVIEWED ? [TAMIL_CLASSES_PAGE] : []),
];

export const NOT_FOUND_PAGE = {
  path: "/404",
  title: "Page Not Found | Western Sydney Tamil Study Centre",
  description: "The page you were looking for could not be found.",
  noindex: true,
  breadcrumbs: [],
};

// Normalise "/programs" and "/programs/" to the same registry key.
const withSlash = (path = "/") => (path.endsWith("/") ? path : `${path}/`);

export const getPage = (path) =>
  PAGES.find((page) => page.path === withSlash(path)) || null;
